import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * A route change starts at the top of the new page, or at the anchor it
 * names: the FAQ links straight to /privacy-policy#apple-health, and a
 * client-side route change does not scroll to a hash on its own. The scroll
 * to the top is instant on purpose: a smooth scroll started while the old
 * page is being torn down is dropped.
 *
 * The scroll to an anchor waits until the browser has laid the page out for
 * its next frame. Asked straight after React's commit, scrollIntoView forced
 * a synchronous layout of the whole freshly mounted page - 40-49 ms of forced
 * reflow on /#waitlist in Lighthouse's mobile profile. A frame callback is
 * still too early (it runs before layout); the task after it is not.
 *
 * The legal pages are loaded on demand, so the anchor may not exist yet:
 * then it waits for it to appear.
 *
 * The anchor of the page a visitor ARRIVES on is reached instantly, as a
 * browser lands on an anchor. index.css's scroll-behavior: smooth turned
 * arriving at /#waitlist into a one-second glide over 5,700 px of page,
 * past the hero film, which then started and downloaded 2 MB for a visitor
 * who never saw it. An anchor chosen on the page keeps the glide.
 */
const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  const arriving = useRef(true);
  useEffect(() => {
    if (!hash) {
      arriving.current = false;
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      return undefined;
    }
    const id = decodeURIComponent(hash.slice(1));
    let frame = null;
    let timer = null;
    let watcher = null;

    const afterLayout = (fn) => {
      frame = window.requestAnimationFrame(() => {
        timer = setTimeout(fn, 0);
      });
    };
    const scroll = () => {
      const target = document.getElementById(id);
      if (target) {
        // Cleared only when it has scrolled: StrictMode's rehearsal of this
        // effect is cancelled before its frame and must not use it up.
        target.scrollIntoView(arriving.current ? { behavior: 'instant' } : undefined);
        arriving.current = false;
        return;
      }
      watcher = new MutationObserver(() => {
        if (!document.getElementById(id)) return;
        watcher.disconnect();
        watcher = null;
        afterLayout(scroll);
      });
      watcher.observe(document.body, { childList: true, subtree: true });
    };

    afterLayout(scroll);
    return () => {
      if (frame !== null) window.cancelAnimationFrame(frame);
      clearTimeout(timer);
      if (watcher) watcher.disconnect();
    };
  }, [pathname, hash]);
  return null;
};

export default ScrollToTop;
